import {
    Badge,
    Box,
    Button,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Menu,
    Typography,
    useMediaQuery,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import { toJalaaliDateTime } from "@/utils/datetime";
import { useTheme } from "@emotion/react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
    BELL_UNSEEN_NOTIFCATIONS,
    BELL_UNSEEN_NOTIFICATIONS_COUNT,
} from "@/graphql/queries/notifications";
import { parseDataWithEdges } from "@/utils/graphql";
import AppProgress from "./AppProgress";
import { useDebounce } from "../hooks/useDebounce";
import {
    MARK_BELL_NOTIFICATION_READ,
    MARK_BELL_NOTIFICATION_READ_ALL,
} from "@/graphql/mutations/notifications";
import { errorToast, successToast } from "@/utils/toasts";
import { useRouter } from "next/router";

export default function BellMenu() {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleOpen = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const theme = useTheme();
    const mobileViewMatches = useMediaQuery(theme.breakpoints.down("sm"));

    const router = useRouter();

    const [fetchBellCounts, { data }] = useLazyQuery(
        BELL_UNSEEN_NOTIFICATIONS_COUNT
    );

    const counts = data?.bellUnseenNotificationsCounts?.counts ?? 0;

    const [markAllRead, { loading: markReadLoading }] = useMutation(
        MARK_BELL_NOTIFICATION_READ_ALL,
        {
            refetchQueries: [
                BELL_UNSEEN_NOTIFICATIONS_COUNT,
                "bellUnseenNotificationsCounts",
                BELL_UNSEEN_NOTIFCATIONS,
                "bellUnseenNotifications",
            ],
            onCompleted: () => {
                successToast("همه پیام ها با  موفقیت حدف شد.");
                setAnchorEl(null);
            },
            onError: (err) => {
                errorToast(err?.message);
            },
        }
    );

    const debounce = useDebounce();

    const fetchBellNotificationCounts = () => {
        debounce(() => {
            fetchBellCounts();
        }, 1000);
    };

    useEffect(() => {
        fetchBellNotificationCounts();
    }, [router.pathname]);

    useEffect(() => {
        fetchBellNotificationCounts();
    }, []);

    return (
        <>
            <Box>
                <IconButton
                    size="medium"
                    onClick={(e) => {
                        handleOpen(e);
                    }}
                >
                    <Badge
                        badgeContent={counts}
                        max={99}
                        color="error"
                        overlap="rectangular"
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "left",
                        }}
                    >
                        <NotificationsNoneIcon
                            color="error"
                            sx={{
                                "&.MuiSvgIcon-root": {
                                    fontSize: "26px",
                                    fill: "gray",
                                },
                            }}
                        />
                    </Badge>
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                paddin: 0,
                                margin: 0,
                                width: "100%",
                                ...(mobileViewMatches
                                    ? { left: "0 !important" }
                                    : {}),
                                ...(mobileViewMatches
                                    ? { right: "0 !important" }
                                    : {}),
                                maxWidth: {
                                    xs: "100%",
                                    sm: "480px",
                                },
                            },
                        },
                    }}
                >
                    {/* List of Notifications */}
                    {open && (
                        <BellNotificationList
                            closeNotificationList={() => setAnchorEl(null)}
                        />
                    )}

                    {/* Fixed Clear All Button */}
                    <Box
                        sx={{
                            position: "sticky", // Sticks to the bottom of the container
                            bottom: 0,
                            backgroundColor: "white", // Ensure it has a background
                            zIndex: 1, // Stays above the list
                            borderTop: "1px solid #ddd", // Optional: Add a border for separation
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "8px 0 2px", // Add some padding for spacing
                        }}
                    >
                        <Button
                            variant="text"
                            sx={{
                                fontSize: "0.87rem",
                                color: "#666",
                            }}
                            endIcon={<ClearAllIcon />}
                            onClick={() => markAllRead()}
                            disabled={markReadLoading || counts == 0}
                            disableRipple
                        >
                            حذف همه پیام ها
                        </Button>
                    </Box>
                </Menu>
            </Box>
        </>
    );
}

function BellNotificationList({ closeNotificationList }) {
    const limit = 10;
    const [offset, setOffset] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [notifications, setNotifications] = useState([]);

    const [fetchItems, { loading }] = useLazyQuery(BELL_UNSEEN_NOTIFCATIONS, {
        variables: { first: 3, offset: offset }, // Use the ref for initial variables
        onCompleted: (data) => {
            const {
                bellUnseenNotifications,
                pageInfo: { hasNextPage = false } = {},
            } = parseDataWithEdges(data, ["bellUnseenNotifications"]) ?? {};

            if (offset == 0) {
                setNotifications((prevNotifications) => [
                    ...bellUnseenNotifications,
                ]);
            } else {
                setNotifications((prevNotifications) => [
                    ...prevNotifications,
                    ...bellUnseenNotifications,
                ]);
            }

            setHasNextPage(hasNextPage);
            if (hasNextPage)
                setOffset(
                    (prevOffset) =>
                        prevOffset + (bellUnseenNotifications?.length ?? 0)
                );
        },
    });

    const debounce = useDebounce();

    const loadMoreItems = (e) => {
        debounce(() => {
            if (loading || !hasNextPage) return;

            const bottom =
                e.target.scrollHeight -
                    Math.ceil(e.target.scrollTop + e.target.clientHeight) <=
                40;

            if (bottom) {
                fetchItems({
                    variables: {
                        first: limit,
                        offset: offset, // Use the latest value from the ref
                    },
                });
            }
        }, 500);
    };

    const [markRead, { loading: markReadLoading }] = useMutation(
        MARK_BELL_NOTIFICATION_READ,
        {
            refetchQueries: [
                BELL_UNSEEN_NOTIFICATIONS_COUNT,
                "bellUnseenNotificationsCounts",
            ],
            onCompleted: () => {
                successToast("پیام با موفقیت حدف شد.");
                setOffset(0);
                setHasNextPage(true);

                if (notifications?.length == 1) {
                    closeNotificationList();
                }

                setTimeout(() => {
                    fetchItems({
                        variables: {
                            first: limit,
                            offset: 0,
                        },
                    });
                }, 500);
            },
            onError: (err) => {
                errorToast(err?.message);
            },
        }
    );

    useEffect(() => {
        fetchItems({
            variables: {
                first: limit,
                offset: offset, // Use the latest value from the ref
            },
        });
    }, []);

    const goToLinks = (link) => {
        try {
            const origin = window?.location?.origin ?? "";
            const url = new URL(link);
            const linkPathName = url.pathname;
            window?.location?.replace(`${origin}${linkPathName}`);
        } catch {}
    };

    return (
        <List
            sx={{
                direction: "ltr",
                maxHeight: "300px",
                overflowY: "auto", // Ensure scroll is enabled
            }}
            onScroll={loadMoreItems}
        >
            {notifications?.map((notification, index) => {
                const isLast = index + 1 === notifications.length;
                return (
                    <>
                        <ListItem
                            key={index}
                            sx={{
                                display: "flex",
                                alignItems: "start",
                                padding: "10px 15px 10px 30px",
                            }}
                        >
                            <ListItemText>
                                <Typography
                                    variant="span"
                                    sx={{
                                        fontSize: "0.75rem",
                                        color: "#888",
                                    }}
                                >
                                    {toJalaaliDateTime(notification.createdAt)}
                                </Typography>

                                {notification?.title && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: "bold",
                                            textAlign: "left",
                                            marginTop: "4px",
                                            marginBottom: "6px",
                                        }}
                                    >
                                        {notification.title}
                                    </Typography>
                                )}

                                <Typography
                                    variant="body2"
                                    sx={{ textAlign: "left" }}
                                >
                                    {notification.text}
                                </Typography>
                                {notification?.link && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() =>
                                            goToLinks(notification?.link)
                                        }
                                        sx={{
                                            marginTop: "8px",
                                            marginBottom: "6px",
                                        }}
                                        disableElevation
                                    >
                                        {notification?.linkText}
                                    </Button>
                                )}
                            </ListItemText>
                            <ListItemIcon
                                sx={{
                                    display: "flex",
                                    justifyContent: "end",
                                    padding: 0,
                                    margin: 0,
                                }}
                            >
                                <IconButton
                                    size="medium"
                                    onClick={() =>
                                        markRead({
                                            variables: {
                                                id: notification?.bellNotificationId,
                                            },
                                        })
                                    }
                                    disabled={markReadLoading}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </ListItemIcon>
                        </ListItem>
                        {!isLast && <Divider />}
                    </>
                );
            })}
            {notifications?.length == 0 && (
                <Box
                    sx={{
                        minHeight: "100px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography
                        sx={{ fontSize: "0.9rem", textAlign: "center" }}
                    >
                        پیامی یافت نشد.
                    </Typography>
                </Box>
            )}
            {loading && <AppProgress minHeight="200px" />}
        </List>
    );
}
